import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { UserfulTooltip } from "userful-chronos-common-ui/dist";
import { ClusterDeviceInfo, IClusterInfo, IConstellationInfo } from "../../../model/CarbonExternalModels";
import { AppSliceActions } from "../../../store/app/appSlice";
import { selectDashboards, useCarbonDispatch, useCarbonSelector } from "../../../store/carbonStore";

import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import DevicesDropdown from "./devicesDropdown/DevicesDropdown";
import DeviceSystemInfo from "./DeviceSystemInfo";

import "./DevicePage.scss";

interface IProps {
    foundObject: { cluster: IClusterInfo; constellation: IConstellationInfo; device: ClusterDeviceInfo };
}

type TDevice = "SYSTEM_INFO" | "NETWORKING" | "GPU" | "CPU" | "STORAGE" | "CAPACITY";

const getDashboardTargetOrigin = (dashboardURL: string): string => {
    const configuredGrafanaURL = process.env.REACT_APP_GRAFANA_URL;
    const targetURL = configuredGrafanaURL || dashboardURL || window.location.origin;

    try {
        return new URL(targetURL, window.location.origin).origin;
    } catch (error) {
        console.warn("Invalid Grafana target URL, falling back to current origin", error);
        return window.location.origin;
    }
};

export default function DevicePage({ foundObject }: IProps) {
    const [clusterView, setDeviceView] = useState<TDevice>("SYSTEM_INFO");

    const dispatch = useCarbonDispatch();

    const dashboards = useCarbonSelector(selectDashboards);
    const dashboard = dashboards[foundObject.cluster.id.value];

    const dashboardRef = useRef<HTMLIFrameElement | null>(null);

    // console.warn(dashboards);
    // console.warn(foundObject);
    // console.warn(dashboard);

    const dashboardURL =
        clusterView === "NETWORKING"
            ? dashboard?.uris?.["NETWORK"] || ""
            : clusterView === "GPU"
              ? dashboard?.uris?.["GPU"] || ""
              : clusterView === "CPU"
                ? dashboard?.uris?.["CPU"] || ""
                : clusterView === "STORAGE"
                  ? dashboard?.uris?.["STORAGE"] || ""
                  : clusterView === "CAPACITY"
                    ? dashboard?.uris?.["CAPACITY"] || ""
                    : "";

    useEffect(() => {
        if (clusterView !== "SYSTEM_INFO" && !dashboardURL) {
            setDeviceView("SYSTEM_INFO");
        }
    }, [clusterView, dashboardURL]);

    const handleDashboardLoad = () => {
        const iframe = dashboardRef.current;

        if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                {
                    Authorization: "Bearer " + getGlobalStates().keycloak.token,
                },
                getDashboardTargetOrigin(dashboardURL),
            );
        }
    };

    return (
        <div className="genericWrapper">
            <div className="actionsBar">
                <div className="cluster normalGap">
                    <UserfulTooltip title={"Return to "}>
                        <button
                            className="iconButton"
                            onClick={(e) => {
                                dispatch(AppSliceActions.setAppView({ type: "CLUSTER", id: foundObject.cluster.id }));
                            }}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>
                    </UserfulTooltip>
                    <DevicesDropdown foundObject={foundObject} />
                    <div className="multiStateWrapper">
                        <button
                            className={`mSbutton ${clusterView === "SYSTEM_INFO" ? "active" : ""}`}
                            onClick={(e) => {
                                setDeviceView("SYSTEM_INFO");
                            }}
                        >
                            <p>System Info</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "NETWORKING" ? "active" : ""}`}
                            disabled={!dashboard || !dashboard.uris}
                            onClick={(e) => {
                                setDeviceView("NETWORKING");
                            }}
                        >
                            <p>Networking</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "GPU" ? "active" : ""}`}
                            disabled={!dashboard || !dashboard.uris}
                            onClick={(e) => {
                                setDeviceView("GPU");
                            }}
                        >
                            <p>GPU</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "CPU" ? "active" : ""}`}
                            disabled={!dashboard || !dashboard.uris}
                            onClick={(e) => {
                                setDeviceView("CPU");
                            }}
                        >
                            <p>CPU</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "STORAGE" ? "active" : ""}`}
                            disabled={!dashboard || !dashboard.uris}
                            onClick={(e) => {
                                setDeviceView("STORAGE");
                            }}
                        >
                            <p>Storage</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "CAPACITY" ? "active" : ""}`}
                            disabled={!dashboard || !dashboard.uris}
                            onClick={(e) => {
                                setDeviceView("CAPACITY");
                            }}
                        >
                            <p>Capacity</p>
                        </button>
                    </div>
                </div>
                <div className="cluster normalGap">
                    {/* <UserfulTooltip title={"Delete"}>
                        <button className="iconButton">
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                    </UserfulTooltip> */}
                </div>
            </div>
            {clusterView === "SYSTEM_INFO" && (
                <div className="device-body">
                    <DeviceSystemInfo foundObject={foundObject} />
                </div>
            )}
            {clusterView !== "SYSTEM_INFO" && dashboardURL && (
                <iframe
                    ref={dashboardRef}
                    className="full-iframe-container"
                    src={dashboardURL}
                    onLoad={handleDashboardLoad}
                />
            )}
        </div>
    );
}
